/**
 * Review queue / worklist — all pending (or otherwise filtered) obligations,
 * filterable and sortable by confidence, regulation, regulated object and
 * tier. Supports chunked, confidence-prioritized processing: high-confidence
 * items can be batch-confirmed (server re-validates the confidence floor),
 * edge cases are opened individually. There is deliberately NO "confirm all".
 */

import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, RefreshCw } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { useUrlPageState } from "../../utilities/dataTablePagination";
import PflichtklasseLegend from "./PflichtklasseLegend";
import {
  ReviewStatusWithEffectiveDate,
  ProvenanceBadge,
  ConfidenceIndicator,
  WrappedText,
  regulatedObjectName,
  regulatedSubjectName,
  regulatedRefName,
} from "./legalDisplay";
import { approvalBlock, useReviewer } from "./fourEyes";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Properties {
  vocabulary: any;
  regulations: any[];
  regulatedObjects: any[];
  refreshSignal: number;
  onOpenObligation: (obligationKey: string) => void;
  onBatchConfirmed: () => void;
}

// Server cap per filter query; the table itself paginates client-side.
const FETCH_LIMIT = 200;

// Filters persist in the URL query so they survive closing an obligation / reload / share.
const FILTER_PARAMS = {
  reviewStatus: "rqStatus",
  regulationId: "rqRegulation",
  regulatedObjectCode: "rqObject",
  type: "rqType",
  tier: "rqTier",
  minConfidence: "rqConfidence",
  withoutHazard: "rqNoHazard",
  sortDir: "rqSort",
} as const;

const readInitialFilters = () => {
  const p = new URL(window.location.href).searchParams;
  const status = p.get(FILTER_PARAMS.reviewStatus);
  return {
    reviewStatus: status === "all" ? "all" : status || "pending",
    regulationId: p.get(FILTER_PARAMS.regulationId) || "all",
    regulatedObjectCode: p.get(FILTER_PARAMS.regulatedObjectCode) || "all",
    type: p.get(FILTER_PARAMS.type) || "all",
    tier: p.get(FILTER_PARAMS.tier) || "all",
    minConfidence: p.get(FILTER_PARAMS.minConfidence) || "all",
    withoutHazard: p.get(FILTER_PARAMS.withoutHazard) || "all",
    sortDir: (p.get(FILTER_PARAMS.sortDir) === "asc" ? "asc" : "desc") as "asc" | "desc",
  };
};

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Ein Filter mit sichtbarer Beschriftung über dem Feld. */
function FilterSelect(properties: {
  id: string;
  label: string;
  width: number;
  value: string;
  options: { key: string; text: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div
      className="flex flex-col gap-1.5"
      style={{ width: `min(${properties.width}px, 100%)` }}
    >
      <Label htmlFor={properties.id}>{properties.label}</Label>
      <Select value={properties.value} onValueChange={properties.onChange}>
        <SelectTrigger id={properties.id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-w-[calc(100vw-2rem)]">
          {properties.options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function ReviewQueuePanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const [, setSearchParams] = useSearchParams();
  const pagination = useUrlPageState("legalReviewQueuePage");
  const fieldId = useId();

  const initialFilters = useMemo(readInitialFilters, []);
  const reviewer = useReviewer();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [reviewStatus, setReviewStatus] = useState(initialFilters.reviewStatus);
  const [regulationId, setRegulationId] = useState(initialFilters.regulationId);
  const [regulatedObjectCode, setRegulatedObjectCode] = useState(initialFilters.regulatedObjectCode);
  const [type, setType] = useState(initialFilters.type);
  const [tier, setTier] = useState(initialFilters.tier);
  const [minConfidence, setMinConfidence] = useState(initialFilters.minConfidence);
  const [withoutHazard, setWithoutHazard] = useState(initialFilters.withoutHazard);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(initialFilters.sortDir);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [batchSkipped, setBatchSkipped] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [checkPhrase, setCheckPhrase] = useState("");
  const [loading, setLoading] = useState(false);

  const batchThreshold = properties.vocabulary?.batchConfirmMinConfidence ?? 0.9;

  const load = async () => {
    const requestParams = {
      reviewStatus,
      regulationId,
      regulatedObjectCode,
      type,
      tier,
      minConfidence,
      withoutHazard,
      sortDir,
    };
    setLoading(true);

    try {
      const tierNum = tier && tier !== "all" ? Number(tier) : undefined;
      const minConfNum = minConfidence && minConfidence !== "all" ? Number(minConfidence) : undefined;
      const result = await legalAPI.getObligations({
        reviewStatus: reviewStatus && reviewStatus !== "all" ? reviewStatus : undefined,
        regulationId: regulationId && regulationId !== "all" ? regulationId : undefined,
        regulatedObjectCode: regulatedObjectCode && regulatedObjectCode !== "all" ? regulatedObjectCode : undefined,
        type: type && type !== "all" ? type : undefined,
        tier: tierNum !== undefined && !Number.isNaN(tierNum) ? tierNum : undefined,
        minConfidence: minConfNum !== undefined && !Number.isNaN(minConfNum) ? minConfNum : undefined,
        withoutHazard: withoutHazard === "true" ? true : undefined,
        sortBy: "confidence",
        sortDir,
        skip: 0,
        limit: FETCH_LIMIT,
      });

      if (
        requestParams.reviewStatus !== reviewStatus ||
        requestParams.regulationId !== regulationId ||
        requestParams.regulatedObjectCode !== regulatedObjectCode ||
        requestParams.type !== type ||
        requestParams.tier !== tier ||
        requestParams.minConfidence !== minConfidence ||
        requestParams.withoutHazard !== withoutHazard ||
        requestParams.sortDir !== sortDir
      ) {
        return;
      }

      setItems(result.items || []);
      setTotal(result.total || 0);
      setSelectedKeys([]);
    } catch (error) {
      if (
        requestParams.reviewStatus === reviewStatus &&
        requestParams.regulationId === regulationId &&
        requestParams.regulatedObjectCode === regulatedObjectCode &&
        requestParams.type === type &&
        requestParams.tier === tier &&
        requestParams.minConfidence === minConfidence &&
        requestParams.withoutHazard === withoutHazard &&
        requestParams.sortDir === sortDir
      ) {
        pushError(error);
      }
    } finally {
      if (
        requestParams.reviewStatus === reviewStatus &&
        requestParams.regulationId === regulationId &&
        requestParams.regulatedObjectCode === regulatedObjectCode &&
        requestParams.type === type &&
        requestParams.tier === tier &&
        requestParams.minConfidence === minConfidence &&
        requestParams.withoutHazard === withoutHazard &&
        requestParams.sortDir === sortDir
      ) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewStatus, regulationId, regulatedObjectCode, type, tier, minConfidence, withoutHazard, sortDir, properties.refreshSignal]);

  /**
   * Die Filter stehen in der Adresse, damit sie das Schließen einer Pflicht, ein Neuladen und das
   * Weitergeben des Verweises überleben.
   *
   * Geschrieben über den Router, nicht über `history.replaceState`. Der Unterschied ist der ganze
   * Fehler: `replaceState` ändert die Adresszeile, ohne dass der Router davon erfährt. Jeder
   * andere Schreiber — allen voran `useUrlPageState`, das die Seitenzahl der Tabelle setzt — baut
   * die neue Adresse aus dem Router-Stand zusammen, und der kannte die Filter nie. Ein Blättern
   * genügte, um sie zu löschen.
   *
   * `replace: true`, weil eine Filteränderung kein Ziel für den Zurück-Knopf ist: Zurück soll aus
   * der Liste führen, nicht durch sieben Zwischenstände der Filterleiste.
   */
  useEffect(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        const sync = (key: string, value: string, def: string) => {
          if (value && value !== def) next.set(key, value);
          else next.delete(key);
        };
        sync(FILTER_PARAMS.reviewStatus, reviewStatus, "pending");
        sync(FILTER_PARAMS.regulationId, regulationId, "all");
        sync(FILTER_PARAMS.regulatedObjectCode, regulatedObjectCode, "all");
        sync(FILTER_PARAMS.type, type, "all");
        sync(FILTER_PARAMS.tier, tier, "all");
        sync(FILTER_PARAMS.minConfidence, minConfidence, "all");
        sync(FILTER_PARAMS.withoutHazard, withoutHazard, "all");
        sync(FILTER_PARAMS.sortDir, sortDir, "desc");
        return next;
      },
      { replace: true },
    );
    // `setSearchParams` ist über Renderzyklen stabil und gehört nicht in die Abhängigkeiten —
    // sonst liefe der Effekt bei jedem Adresswechsel erneut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewStatus, regulationId, regulatedObjectCode, type, tier, minConfidence, withoutHazard, sortDir]);

  // Vier-Augen-Prinzip: the batch gives only the stage the signed-in reviewer may approve —
  // pending items for Fachprüfer Recht, legally approved ones for Fachprüfer FM.
  const isBatchEligible = (obligation: any) =>
    (obligation.reviewStatus === "pending" || obligation.reviewStatus === "legalConfirmed") &&
    typeof obligation.confidence === "number" &&
    obligation.confidence >= batchThreshold &&
    approvalBlock(obligation, reviewer) === null;

  const batchEligibleKeys = useMemo(
    () =>
      items
        .filter(
          (obligation: any) =>
            selectedKeys.includes(obligation.obligationKey) && isBatchEligible(obligation),
        )
        .map((obligation: any) => obligation.obligationKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, selectedKeys, batchThreshold],
  );

  // No selection = confirm ALL eligible items in the current filter result.
  const allEligibleKeys = useMemo(
    () => items.filter(isBatchEligible).map((obligation: any) => obligation.obligationKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, batchThreshold],
  );

  const confirmTargetKeys = selectedKeys.length > 0 ? batchEligibleKeys : allEligibleKeys;
  const checkPhraseExpected = t("operatorResponsibilityPanel.batchConfirmCheckPhrase");

  const withAllOption = (options: { key: string; text: string }[]) => [
    { key: "all", text: t("operatorResponsibilityPanel.all") },
    ...options,
  ];

  const filtersAreDefault =
    reviewStatus === "pending" &&
    regulationId === "all" &&
    regulatedObjectCode === "all" &&
    type === "all" &&
    tier === "all" &&
    minConfidence === "all" &&
    withoutHazard === "all";

  const resetFilters = () => {
    setReviewStatus("pending");
    setRegulationId("all");
    setRegulatedObjectCode("all");
    setType("all");
    setTier("all");
    setMinConfidence("all");
    setWithoutHazard("all");
  };

  const allSelected = items.length > 0 && selectedKeys.length === items.length;

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      // Kopf-Häkchen wählt das gesamte geladene Ergebnis, nicht nur die
      // sichtbare Seite — die Sammelbestätigung arbeitet auf derselben Menge.
      header: () => (
        <Checkbox
          checked={allSelected}
          aria-label={t("operatorResponsibilityPanel.selectAllObligations")}
          onCheckedChange={(checked) =>
            setSelectedKeys(
              checked === true ? items.map((obligation: any) => obligation.obligationKey) : [],
            )
          }
        />
      ),
      size: 44,
      enableSorting: false,
      cell: ({ row }) => (
        <Checkbox
          checked={selectedKeys.includes(row.original.obligationKey)}
          aria-label={`${t("operatorResponsibilityPanel.selectObligation")}: ${row.original.obligationKey}`}
          onCheckedChange={(checked) =>
            setSelectedKeys((previous) =>
              checked === true
                ? [...previous, row.original.obligationKey]
                : previous.filter((key) => key !== row.original.obligationKey),
            )
          }
        />
      ),
    },
    {
      // Pflicht (header) + Beschreibung (underneath) in one column.
      id: "obligation",
      accessorFn: (obligation: any) => obligation.obligationKey,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.obligation")}</SortableHeader>
      ),
      size: 340,
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <button
            type="button"
            className="text-left font-semibold text-primary underline underline-offset-2 hover:no-underline"
            onClick={() => properties.onOpenObligation(row.original.obligationKey)}
          >
            {row.original.obligationKey}
          </button>
          <WrappedText text={row.original.description} maxWidth={360} fontSize={12} />
        </div>
      ),
    },
    {
      id: "regulatedObject",
      accessorFn: (obligation: any) => regulatedRefName(t, obligation),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("operatorResponsibilityPanel.regulatedObject")}
        </SortableHeader>
      ),
      size: 160,
      cell: ({ row }) => (
        <WrappedText text={regulatedRefName(t, row.original)} maxWidth={150} fontSize={12} />
      ),
    },
    {
      id: "tier",
      accessorFn: (obligation: any) => obligation.tier ?? "—",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.tier")}</SortableHeader>
      ),
      size: 70,
    },
    {
      id: "type",
      accessorFn: (obligation: any) =>
        t(`operatorResponsibilityPanel.obligationType.${obligation.type}`),
      header: t("operatorResponsibilityPanel.type"),
      size: 140,
      enableSorting: false,
    },
    {
      id: "confidence",
      accessorFn: (obligation: any) => obligation.confidence,
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.confidence")}</SortableHeader>
      ),
      size: 110,
      cell: ({ row }) => <ConfidenceIndicator value={row.original.confidence} />,
    },
    {
      id: "provenance",
      header: t("operatorResponsibilityPanel.provenance.label"),
      size: 190,
      enableSorting: false,
      cell: ({ row }) => (
        <ProvenanceBadge
          provenanceType={row.original.provenanceType}
          generatedBy={row.original.generatedBy}
        />
      ),
    },
    {
      id: "status",
      header: t("operatorResponsibilityPanel.status"),
      enableSorting: false,
      cell: ({ row }) => <ReviewStatusWithEffectiveDate obligation={row.original} />,
    },
    {
      // Explicit open action — the ONLY way to open a row (row-click removed so touch
      // swipes never open an obligation by accident).
      id: "open",
      header: () => <span className="sr-only">{t("operatorResponsibilityPanel.openObligation")}</span>,
      size: 56,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          title={t("operatorResponsibilityPanel.openObligation")}
          aria-label={`${t("operatorResponsibilityPanel.openObligation")}: ${row.original.obligationKey}`}
          onClick={() => properties.onOpenObligation(row.original.obligationKey)}
        >
          <Pencil aria-hidden className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <PflichtklasseLegend />

      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <FilterSelect
          id={`${fieldId}-status`}
          label={t("operatorResponsibilityPanel.status")}
          width={180}
          value={reviewStatus}
          onChange={setReviewStatus}
          options={withAllOption(
            (properties.vocabulary?.reviewStatuses || []).map((status: string) => ({
              key: status,
              text: t(`operatorResponsibilityPanel.reviewStatus.${status}`),
            })),
          )}
        />
        <FilterSelect
          id={`${fieldId}-regulation`}
          label={t("operatorResponsibilityPanel.regulation")}
          width={200}
          value={regulationId}
          onChange={setRegulationId}
          options={withAllOption(
            // Der Server sortiert nach eliId, angezeigt wird shortName — nach Kürzel gelesen
            // wirkt das zufällig (erst alle de/bund/…, dann din/…, en/…, eu/reg/…). Sortiert
            // wird deshalb nach dem, was dasteht, wie beim Objekt-Filter darunter.
            (properties.regulations || [])
              .map((regulation: any) => ({
                key: regulation.eliId,
                text: regulation.shortName || regulation.eliId,
              }))
              .sort((a: any, b: any) => a.text.localeCompare(b.text, "de")),
          )}
        />
        <FilterSelect
          id={`${fieldId}-object`}
          label={t("operatorResponsibilityPanel.regulatedObject")}
          width={220}
          value={regulatedObjectCode}
          onChange={setRegulatedObjectCode}
          // Beide Achsen in einer Liste — der Filter fragt "woran hängt die Pflicht?", und die
          // Antwort kann eine Anlage oder eine Subjektklasse sein. Ohne die Subjekte hier
          // verschwinden 59 Pflichten aus jeder anlagenorganisierten Ansicht, ohne dass es
          // jemandem auffällt.
          options={withAllOption(
            [
              ...(properties.regulatedObjects || []).map((object: any) => ({
                key: object.code,
                text: regulatedObjectName(t, object.code, object.name),
              })),
              ...((properties.vocabulary?.regulatedSubjects || []) as string[]).map((code) => ({
                key: code,
                text: regulatedSubjectName(t, code),
              })),
            ].sort((a: any, b: any) => a.text.localeCompare(b.text, "de")),
          )}
        />
        <FilterSelect
          id={`${fieldId}-type`}
          label={t("operatorResponsibilityPanel.type")}
          width={180}
          value={type}
          onChange={setType}
          options={withAllOption(
            (properties.vocabulary?.obligationTypes || []).map((value: string) => ({
              key: value,
              text: t(`operatorResponsibilityPanel.obligationType.${value}`),
            })),
          )}
        />
        <FilterSelect
          id={`${fieldId}-tier`}
          label={t("operatorResponsibilityPanel.tier")}
          width={130}
          value={tier}
          onChange={setTier}
          options={withAllOption(
            (properties.vocabulary?.tiers || []).map((value: number) => ({
              key: String(value),
              text: t("operatorResponsibilityPanel.tierLegend.tierLabelShort", { tier: value }),
            })),
          )}
        />
        <FilterSelect
          id={`${fieldId}-confidence`}
          label={t("operatorResponsibilityPanel.minConfidence")}
          width={150}
          value={minConfidence}
          onChange={setMinConfidence}
          options={withAllOption([
            { key: "0.9", text: "≥ 90%" },
            { key: "0.8", text: "≥ 80%" },
            { key: "0.7", text: "≥ 70%" },
          ])}
        />
        {/* Gap queue (SPEC_Hazard_UI §5) — an obligation with no hazard is the
            strongest signal that the catalogue is incomplete. */}
        <FilterSelect
          id={`${fieldId}-hazard`}
          label={t("operatorResponsibilityPanel.hazardAssignment")}
          width={240}
          value={withoutHazard}
          onChange={setWithoutHazard}
          options={[
            { key: "all", text: t("operatorResponsibilityPanel.hazardAssignmentAll") },
            { key: "true", text: t("operatorResponsibilityPanel.hazardAssignmentMissing") },
          ]}
        />
        <FilterSelect
          id={`${fieldId}-sort`}
          label={t("operatorResponsibilityPanel.sortByConfidence")}
          width={180}
          value={sortDir}
          onChange={(value) => setSortDir(value as "asc" | "desc")}
          options={[
            { key: "desc", text: t("operatorResponsibilityPanel.highestFirst") },
            { key: "asc", text: t("operatorResponsibilityPanel.lowestFirst") },
          ]}
        />
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <Button
          className="max-sm:w-full"
          disabled={confirmTargetKeys.length === 0 || (selectedKeys.length === 0 && total > items.length)}
          onClick={() => {
            setCheckPhrase("");
            setConfirmDialogOpen(true);
          }}
        >
          {selectedKeys.length > 0
            ? t("operatorResponsibilityPanel.batchConfirm", { count: batchEligibleKeys.length })
            : t("operatorResponsibilityPanel.batchConfirmAll")}
        </Button>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("operatorResponsibilityPanel.batchConfirmHint", {
            threshold: Math.round(batchThreshold * 100),
          })}
        </p>
      </div>

      {/* Übersprungene Pflichten bleiben stehen: Sie sind das Ergebnis, das
          weiter bearbeitet werden muss — ein Toast wäre dafür zu flüchtig. */}
      {batchSkipped > 0 ? (
        <Alert variant="warning">
          <AlertDescription className="min-w-0 break-words">
            {t("operatorResponsibilityPanel.batchSkipped", { count: batchSkipped })}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={items}
          loading={loading}
          pageSize={5}
          page={pagination.page}
          onPageChange={pagination.onPageChange}
          previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
          nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
          emptyMessage={
            <span className="flex flex-col items-center gap-2 py-2">
              <span className="text-sm font-semibold text-foreground">
                {filtersAreDefault
                  ? t("operatorResponsibilityPanel.noPendingObligations")
                  : t("operatorResponsibilityPanel.noObligationsForFilter")}
              </span>
              <span className="max-w-prose text-sm">
                {filtersAreDefault
                  ? t("operatorResponsibilityPanel.noPendingObligationsHint")
                  : t("operatorResponsibilityPanel.noObligationsForFilterHint")}
              </span>
              {!filtersAreDefault ? (
                <Button variant="outline" size="sm" onClick={resetFilters}>
                  {t("operatorResponsibilityPanel.resetFilters")}
                </Button>
              ) : null}
            </span>
          }
        />
      </div>

      <div className="flex min-w-0 flex-wrap items-center gap-3">
        {total > items.length ? (
          <p className="min-w-0 break-words text-sm text-warning">
            {t("operatorResponsibilityPanel.truncatedResult", { shown: items.length, total })}
          </p>
        ) : null}
        <Button variant="ghost" size="sm" className="gap-2 px-2" onClick={() => load()}>
          <RefreshCw aria-hidden className="h-4 w-4" />
          {t("global.refresh")}
        </Button>
      </div>

      {/* Sammelbestätigung schreibt viele protokollierte Entscheidungen auf
          einmal — deshalb der getippte Merksatz als zweite Hürde (wie im
          Bestand) und die Folge im Text. */}
      <AlertDialog
        open={confirmDialogOpen}
        onOpenChange={(open) => {
          setConfirmDialogOpen(open);
          if (!open) setCheckPhrase("");
        }}
      >
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("operatorResponsibilityPanel.batchConfirmDialogTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {t("operatorResponsibilityPanel.batchConfirmDialogMessage", {
                count: confirmTargetKeys.length,
                threshold: Math.round(batchThreshold * 100),
                phrase: checkPhraseExpected,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-check-phrase`}>
              {t("operatorResponsibilityPanel.batchConfirmCheckLabel", {
                phrase: checkPhraseExpected,
              })}
            </Label>
            <Input
              id={`${fieldId}-check-phrase`}
              value={checkPhrase}
              placeholder={checkPhraseExpected}
              onChange={(event) => setCheckPhrase(event.target.value)}
            />
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              disabled={checkPhrase !== checkPhraseExpected}
              onClick={async () => {
                setConfirmDialogOpen(false);
                setCheckPhrase("");

                try {
                  const result = await legalAPI.batchConfirmObligations(confirmTargetKeys);

                  setBatchSkipped(result?.skipped?.length || 0);
                  await load();
                  properties.onBatchConfirmed();
                  pushSuccess(
                    t("operatorResponsibilityPanel.batchConfirmed", {
                      count: result?.confirmed?.length || 0,
                    }),
                  );
                } catch (error) {
                  pushError(error);
                }
              }}
            >
              {t("operatorResponsibilityPanel.batchConfirmDialogTitle")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
